import { useCallback, useMemo } from "react";
import { useBodies, useWorldBodies } from "../stores/sceneStore";
import { useSelectionStore } from "../stores/selectionStore";
import { buildBodyRootMap } from "../utils/bodyHierarchy";
import { requestInspectorChromeFlash } from "../utils/inspectorChromeFlash";
import type { BodyUUID } from "../types/scene";

/**
 * Resolves the world-body root for a scene body and opens the Object inspector
 * (same selection semantics as scene tree picking a world root).
 */
export function useNavigateToObjectFromBodyUuid(bodyUuid: string | null | undefined) {
  const bodies = useBodies();
  const worldBodies = useWorldBodies();

  const objectRootUuid = useMemo(() => {
    if (!bodyUuid) return null;
    const key = bodyUuid as BodyUUID;
    const rootMap = buildBodyRootMap(bodies || {}, worldBodies || []);
    return rootMap[key] || key;
  }, [bodyUuid, bodies, worldBodies]);

  const onNavigateToObject = useCallback(() => {
    if (!objectRootUuid) return;
    const { setLastSelectionSource, selectBody, selectedBody } = useSelectionStore.getState();
    requestInspectorChromeFlash();
    setLastSelectionSource("sceneTreeObject");
    if (selectedBody !== objectRootUuid) {
      selectBody(String(objectRootUuid));
    }
  }, [objectRootUuid]);

  return { objectRootUuid, onNavigateToObject };
}
